import { api } from '../features/auth/authStore';
import { Plan } from './plan.service';

export interface TenantPlan {
  _id: string;
  tenantId: string;
  planId: string | Plan;
  planName: string;
  planCost: number;
  planCurrency: string;
  planDuration: string;
  startDate: string;
  endDate: string;
  activatedAt?: string;
  cancelledAt?: string;
  status: 'active' | 'trial' | 'expired' | 'suspended' | 'cancelled';
  autoRenew: boolean;
  paymentStatus: 'pending' | 'paid' | 'failed' | 'refunded' | 'cancelled';
  paymentMethod: 'manual' | 'stripe' | 'paypal' | 'moneris' | 'bambora';
  paymentDate?: string;
  transactionId?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  stripeInvoiceId?: string;
  isTrial: boolean;
  trialStartDate?: string;
  trialEndDate?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AssignPlanData {
  tenantId: string;
  planId: string;
  startDate: string;
  notes?: string;
}

export interface PurchasePlanData {
  planId: string;
}

class TenantPlanService {
  /**
   * Get current plan for logged-in tenant
   */
  async getCurrentPlan(): Promise<{ data: TenantPlan | null }> {
    const response = await api.get('/tenant-plans/current');
    return response.data;
  }

  /**
   * Get current plan for a specific tenant (SuperAdmin)
   */
  async getCurrentPlanByTenant(tenantId: string): Promise<{ data: TenantPlan | null }> {
    const response = await api.get(`/tenant-plans/current/${tenantId}`);
    return response.data;
  }

  /**
   * Get plan history for a tenant
   */
  async getTenantPlanHistory(tenantId: string): Promise<{ data: TenantPlan[] }> {
    const response = await api.get(`/tenant-plans/history/${tenantId}`);
    return response.data;
  }

  /**
   * Assign plan to tenant manually (SuperAdmin)
   */
  async assignPlanToTenant(
    tenantId: string,
    planId: string,
    startDate: Date,
    notes?: string
  ): Promise<{ data: TenantPlan }> {
    const response = await api.post('/tenant-plans/assign', {
      tenantId,
      planId,
      startDate: startDate.toISOString(),
      notes
    });
    return response.data;
  }

  /**
   * Purchase a plan (online payment)
   */
  async purchasePlan(data: PurchasePlanData): Promise<{
    tenantPlan: TenantPlan;
    checkoutUrl?: string;
  }> {
    const response = await api.post('/tenant-plans/purchase', data);
    return response.data.data;
  }

  /**
   * Cancel a tenant plan
   */
  async cancelTenantPlan(
    planId: string,
    cancelImmediately: boolean = false
  ): Promise<TenantPlan> {
    const response = await api.post(`/tenant-plans/${planId}/cancel`, {
      cancelImmediately,
    });
    return response.data.data;
  }

  /**
   * Renew a tenant plan
   */
  async renewTenantPlan(
    planId: string,
    paymentMethod: string = 'manual'
  ): Promise<TenantPlan> {
    const response = await api.post(`/tenant-plans/${planId}/renew`, {
      paymentMethod,
    });
    return response.data.data;
  }

  /**
   * Get expiring plans (SuperAdmin)
   */
  async getExpiringPlans(days: number = 7): Promise<TenantPlan[]> {
    const response = await api.get(`/tenant-plans/expiring?days=${days}`);
    return response.data.data;
  }

  /**
   * Auto-expire plans (SuperAdmin/Cron)
   */
  async autoExpirePlans(): Promise<{ count: number }> {
    const response = await api.post('/tenant-plans/auto-expire');
    return { count: response.data.count };
  }

  /**
   * Get auth from Stripe callback (handles both success and cancel)
   */
  async getAuthFromCallback(sessionId?: string): Promise<{
    user: any;
    tenant: any;
    tokens: { accessToken: string; refreshToken: string };
  }> {
    const url = sessionId
      ? `/tenant-plans/auth-from-callback?sessionId=${sessionId}`
      : '/tenant-plans/auth-from-callback';
    const response = await api.get(url);
    return response.data.data;
  }
}

export const tenantPlanService = new TenantPlanService();
